// Assignment Node Component for Flow Builder

import React from 'react';
import { Handle, Position } from 'reactflow';
import {
  Box,
  Typography,
  Chip
} from '@mui/material';

interface AssignmentNodeProps {
  data: {
    label: string;
    description?: string;
    assignments?: Array<{
      variable: string;
      value: string;
      operation: string;
    }>;
    status?: string;
    color?: string;
  };
  selected?: boolean;
}

const AssignmentNode: React.FC<AssignmentNodeProps> = ({ data, selected }) => {
  const { 
    label = 'Assignment', 
    description = 'Set variable values',
    assignments = [],
    status = 'idle',
    color = '#45B7D1'
  } = data;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#4CAF50';
      case 'failed': return '#F44336';
      case 'running': return '#FF9800';
      default: return '#9E9E9E';
    }
  };

  return (
    <Box
      sx={{
        minWidth: 200,
        maxWidth: 300,
        background: 'linear-gradient(135deg, rgba(69, 183, 209, 0.1) 0%, rgba(69, 183, 209, 0.2) 100%)',
        border: selected ? `3px solid ${color}` : `2px solid ${color}`,
        borderRadius: '12px',
        padding: 2,
        boxShadow: selected 
          ? `0 8px 25px rgba(69, 183, 209, 0.4)` 
          : '0 4px 12px rgba(0,0,0,0.1)',
        transition: 'all 0.2s ease',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          boxShadow: '0 8px 25px rgba(69, 183, 209, 0.3)',
          transform: 'translateY(-2px)'
        }
      }}
    >
      {/* Top decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${color} 0%, rgba(69, 183, 209, 0.6) 100%)`
        }}
      />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            backgroundColor: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.2rem',
            color: 'white',
            fontWeight: 'bold'
          }}
        >
          =
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ color: '#666', lineHeight: 1 }}>
            Assignment
          </Typography>
        </Box>
        <Chip
          size="small"
          label={status}
          sx={{
            backgroundColor: getStatusColor(status),
            color: 'white',
            fontSize: '0.7rem',
            height: 20
          }}
        />
      </Box>

      {/* Description */}
      <Typography 
        variant="body2" 
        sx={{ 
          color: '#555', 
          mb: 1.5, 
          fontSize: '0.8rem',
          lineHeight: 1.3
        }}
      >
        {description}
      </Typography>

      {/* Assignments Preview */}
      {assignments.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold' }}>
            Variables ({assignments.length}):
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            {assignments.slice(0, 2).map((assignment, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#333', fontWeight: 'medium' }}>
                  {assignment.variable}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {assignment.operation}
                </Typography>
                <Typography variant="caption" sx={{ color: '#333' }}>
                  {assignment.value}
                </Typography>
              </Box>
            ))}
            {assignments.length > 2 && (
              <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic' }}>
                +{assignments.length - 2} more...
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Connection Handles */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: color,
          border: '2px solid white',
          width: 12,
          height: 12
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: color,
          border: '2px solid white',
          width: 12,
          height: 12
        }}
      />
    </Box>
  );
};

export default AssignmentNode;